/*
 * Copyright (c)  2020 CalAmp Corp.  All Rights Reserved
 */
/* eslint-disable max-len */
import flatten from 'lodash.flatten';
import isundefined from 'lodash.isundefined';
import {
  AlertFired,
  GenericObject,
  Link
} from '../types/entities';
import {
  GetAlertsFiredParams,
  Module,
  Service
} from '../types/services';
import {batch} from '../utils/ArrayUtil';
import {convertBase64ToString} from '../utils/Encoding';
import {
  client,
  entityId,
  parse,
  parseCollection,
  url
} from './ConnectUtils';
import {
  getAlertStatus
} from './MicroServices';
import {applyInterceptor} from './ReadOnlyInterceptor';

// defines the maximum number of alerts that can be acknowledged in a single call ...
const MAX_ALERT_ACKNOWLEDGEMENT_SIZE = 50;

/**
 * setAlertsReadOnlyMode sets a restriction to provided restrctions within the function
 * by subscribing to intercepter with applyInterceptor function.
 *
 * ```typescript
 *  import setAlertsReadOnlyMode from '@ion/core/src/services/Alerts';
 *
 *  setAlertsReadOnlyMode(true) // sets restrction on defined urls in restrction array
 *
 * ```
 */
export const setAlertsReadOnlyMode = (isReadOnly = false) => {
  if (isReadOnly) {
    const restrictions = [
      url('/alertsfired/update/:id', Service.Alerts)
    ];
    applyInterceptor({client, restrictions, module: Module.AlertsModule, isReadOnly});
  }
};

/**
 * @function getAlertsFired
 *
 * Retrieves AlertsFired which have triggered within the specified date range,
 * By default, both acknowledged and unacknowledged alerts are returned for the
 * past week.
 *
 * ```typescript
 *
 *    import {getAlertsFired} from '@ion/core/src/services/Alerts';
 *
 *    const alerts = await getAlertsFired(accountIds);
 *
 * ```
 *
 */
export const getAlertsFired = async({
  accountIds,
  createdOn = '[NOW-7DAYS TO NOW]',
  sort = ['-createdOn'],
  api = '2.2',
  pg = 1,
  pageSize = 100,
  maxResults = 999,
  isAcknowledged,
  dateRange,
  notificationType,
  filters = {},
  pageId,
  isSubAccount,
  requiredMetaData,
  includeIsAcknowledged = false
}: GetAlertsFiredParams): Promise<any> => {
  const search = {
    maxResults,
    sort,
    searchTerms : {
      accountId     : accountIds.toString(),
      isAcknowledged,
      createdOn,
      alertTypeName : '*',
      ...filters
    }
  };
  if (notificationType) {
    search.searchTerms.alertTypeName =  notificationType;
  }
  if (dateRange) {
    search.searchTerms.createdOn = `[${dateRange.startDate} TO ${dateRange.endDate}]`;
  }
  if (!includeIsAcknowledged && isundefined(isAcknowledged)) {
    delete search.searchTerms.isAcknowledged;
  }
  let endpoint = url(`/alertsfired/search?v=${api}&pg=${pageId ?? pg}&pgsize=${pageSize}`, Service.Alerts);
  if (!isundefined(isSubAccount)) {
    endpoint = endpoint+`&hierarchy=${isSubAccount}`;
  }
  const response = await client.post(endpoint, JSON.stringify({search})
  );

  return parseCollection(response, 'alertsFired', requiredMetaData);
};

/**
 * @function getLatestAlertsFired
 *
 * Retrieves all Alerts Fired from the given start date (ISO8601 string) through the
 * time of the request (i.e. 'NOW'). If the optional start date is not provided, the
 * default '[NOW-7DAYS TO NOW]' will be used.
 *
 * ```typescript
 *
 *    import {getLatestAlerts} from '@ion/core/src/services/Alerts';
 *
 *    const latest = await getLatestAlerts(accountIds);
 *
 * ```
 *
 */
export const getLatestAlerts = async(accountIds: number[], start?: string): Promise<any> => {
  return await getAlertsFired({accountIds, createdOn: `[${start || 'NOW-7DAYS'} TO *]`});
};

/**
 * @function getUnacknowledgedAlerts
 *
 * Retrieves all Unacknowledged Alerts which have triggered within the specified date
 * range / createdOn argument, (defaults to past week).
 *
 * ```typescript
 *
 *    import {getUnacknowledgedAlerts} from '@ion/core/src/services/Alerts';
 *
 *    const unacknowledgedAlerts = await getUnacknowledgedAlerts(accountIds);
 *
 * ```
 *
 */
export const getUnacknowledgedAlerts = async(params: GetAlertsFiredParams): Promise<any> => {
  return await getAlertsFired({...params, isAcknowledged: false});
};

/**
 * @function getAcknowledgedAlerts
 *
 * Retrieves all Acknowledged Alerts which have triggered within the specified date
 * range / createdOn argument, (defaults to past week).
 *
 * ```typescript
 *
 *    import {getAcknowledgedAlerts} from '@ion/core/src/services/Alerts';
 *
 *    const acknowledgedAlerts = await getAcknowledgedAlerts(accountIds);
 *
 * ```
 *
 */
export const getAcknowledgedAlerts = async(params: GetAlertsFiredParams): Promise<any> => {
  return await getAlertsFired({...params, isAcknowledged: true});
};

/**
 * @function acknowledgeAlert
 *
 * Given a Triggered Alert, updates the Alert's isAcknowledged status to true and
 * returns the result.
 *
 * ```typescript
 *
 *    import {acknowledgeAlert} from '@ion/core/src/services/Alerts';
 *
 *    const acknowledgedAlert = await acknowledgeAlert(unacknowledgedAlert);
 *
 * ```
 *
 */
export const acknowledgeAlert = async(alert: AlertFired, api = '2.2'): Promise<any> => {
  const response = await client.put(url(`/alertsfired/update/${alert.id}?v=${api}`, Service.Alerts),
    JSON.stringify({alert})
  );
  return parse(response, 'alertsFired');
};

/**
 * @function acknowledgeAlerts
 *
 * Given an Account id and list of Triggered Alerts, updates the Alert's isAcknowledged
 * status to true and returns the results.
 *
 * ```typescript
 *
 *    import {acknowledgeAlerts} from '@ion/core/src/services/Alerts';
 *
 *    const acknowledgedAlerts = await acknowledgeAlerts(account._self, alertIds);
 *
 * ```
 *
 */
export const acknowledgeAlerts = async(alertsFiredIds: string[] | number[], account?: Link, api = '2.2'): Promise<any> => {
  const batches = batch(alertsFiredIds, MAX_ALERT_ACKNOWLEDGEMENT_SIZE);
  Promise.all(
    batches.map(async(ids: string[]) => {
      const response = await client.post(url(`/alertsfired/acknowledge/?v=${api}`, Service.Alerts),
        JSON.stringify(account ? {account, alertsFiredIds: ids} : {alertsFiredIds: ids})
      );
      return parseCollection(response, 'alertsFired');
    })
  );
};

/**
 * @function clearAlert
 *
 * Given a Triggered Alert, updates the Alert's cleared status to true and returns
 * the result.
 *
 * ```typescript
 *
 *    import {clearAlert} from '@ion/core/src/services/Alerts';
 *
 *    const clearedAlert = await clearAlert(alert);
 *
 * ```
 *
 */
// [CORE TODO]: NOTE, this is a placeholder pending implementation of the corresponding API ...
export const clearAlert = async(alert: AlertFired, api = '2.2'): Promise<any> => {
  const response = await client.put(url(`/alertsfired/update/${alert.id}?v=${api}`, Service.Alerts),
    JSON.stringify({alert})
  );
  return parse(response, 'alertsFired');
};

/**
 * @function clearAlerts
 *
 * Given an Account id and list of Triggered Alerts, updates the Alert's cleared
 * status to true and returns the results.
 *
 * ```typescript
 *
 *    import {clearAlerts} from '@ion/core/src/services/Alerts';
 *
 *    const clearedAlerts = await clearAlerts(account._self, alertIds);
 *
 * ```
 *
 */
// [CORE TODO]: NOTE, this is a placeholder pending implementation of the corresponding API ...
export const clearAlerts = async(alertsFiredIds: string[], account?: Link, api = '2.2'): Promise<any> => {
  const response = await client.post(url(`/alertsfired/acknowledge/?v=${api}`, Service.Alerts),
    JSON.stringify(account ? {account, alertsFiredIds} : {alertsFiredIds})
  );
  return parseCollection(response, 'alertsFired');
};

/**
 * @function getAlertDetails
 *
 * Provides a convenience function which retrieves the status of all Alert Actions
 * for the specified alert and augments the alert with a details property by action
 * type (email, sms, etc.).
 *
 * ```typescript
 *
 *    import {getAlertDetails} from '@ion/core/src/services/Alerts';
 *
 *    const {email, sms} = await getAlertDetails(alert);
 *
 * ```
 *
 */
export const getAlertDetails = async({alertActions}: AlertFired, decode = false): Promise<any> => {
  const requests = alertActions.map(({notificationUUID}) => getAlertStatus(notificationUUID));
  const response = await Promise.all(requests);
  const details: GenericObject = {};
  if (response) {
    flatten(response).forEach(({email, sms}) => {
      if (email) {
        details.email = decode ? {...email, body: convertBase64ToString(email.body)} : email;
      } else if (sms) {
        details.sms = decode ? {...sms, body: convertBase64ToString(sms.body)} : sms;
      }
    });
  }
  return details;
};

/**
 * @function getAlertType
 *
 * Retrieves an Alert Type for the given AlertFired id instance.
 *
 * ```typescript
 *
 *    import {getAlertType} from '@ion/core/src/services/Alerts';
 *
 *    const alertType = await getAlertType(alert);
 *
 * ```
 *
 */
export const getAlertType = async({alertType}: AlertFired): Promise<any> => {
  const response = await client.get(url(`/alerts/types/${entityId(alertType)}`));
  return parse(response, 'alertType');
};

/**
 * Provides a paginated search implementation against CTC Alert Services.
 *
 * ```typescript
 *
 *    import {loadAlertHistory} from '@ion/core/src/services/Connect';
 *
 *    // retrieve the first 20 alerts within the given account ...
 *    const {alerts, totalCount} = await loadAlertHistory({
 *      accountId        : 321
 *    });
 *
 * ```
 *
 * @param   {number} deviceIdentifier The id of the device from which to retrieve alertsFired entities.
 * @param   {number} accountId The id of the account from which to retrieve alertsFired entities.
 * @param   {number} pageId The page which is to be loaded.
 * @param   {number} pageSize The size of the page to be loaded.
 * @returns Promise<any>
 */
export const loadAlertHistory = async({
  esn,
  deviceIdentifier,
  createdOn,
  isAcknowledged,
  isSubAccount,
  sort = ['-createdOn'],
  api = '2.2',
  pg = 1,
  pageSize = 20,
  maxResults = 1000
}: GetAlertsFiredParams): Promise<any> => {
  const searchTerms = {
    esn,
    deviceIdentifier,
    createdOn,
    isAcknowledged
  };
  const properties = {createdOn: true, isAcknowledged: true, esn: true, deviceIdentifier: true};
  Object.keys(properties).forEach(prop => {
    if (isundefined(searchTerms[prop]) || searchTerms[prop] === null) {
      delete searchTerms[prop];
    }
  });
  const search = {
    maxResults,
    sort,
    searchTerms
  };
  let endpoint = url(`/alertsfired/search?v=${api}&pg=${pg}&pgsize=${pageSize}`, Service.Alerts);
  
  if (!isundefined(isSubAccount)) {
    endpoint = endpoint+`&hierarchy=${isSubAccount}`;
  }
  const response = await client.post(endpoint, JSON.stringify({search}));
  const {results, totalCount, _links} = response.data?.response;
  return {
    items : results?.map(({alertsFired}) => alertsFired),
    totalCount,
    _links
  };
};
